<!-- ValueSetCard.vue -->
<template>
  <div class="card mb-4">
    <header class="card-header cursor-pointer" @click="toggleExpanded">
      <p class="card-header-title">
        {{ valueSet.name }} (updated {{ formatDate(valueSet.updated_at) }})
      </p>
      <span class="card-header-icon">
        <span class="icon">
          <i :class="['fas', isExpanded ? 'fa-angle-up' : 'fa-angle-down']"></i>
        </span>
      </span>
    </header>

    <div class="card-content" v-show="isExpanded">
      <div class="content">
        <div class="mb-4">
          <span class="has-text-grey">Owner: {{ valueSet.advisor_name }}</span>
        </div>
        <div class="mb-4">
          <span class="has-text-grey">Created on {{ formatDate(valueSet.created_at) }}</span>
          <span class="has-text-grey">Updated on {{ formatDate(valueSet.updated_at) }}</span>
        </div>

        <!-- Permissions section -->
        <div class="mb-4" v-if="isEditable">
          <h4 class="title is-6">Permissions</h4>
          <div v-if="isLoadingPermissions">
            Loading permissions...
          </div>
          <table v-else-if="permissions" class="table is-fullwidth">
            <thead>
              <tr>
                <th>Email</th>
                <th>Can View</th>
                <th>Can Edit</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in permissions" :key="user.id">
                <td>{{ user.email }}</td>
                <td>
                  <input type="checkbox" 
                         :checked="user.permissions.includes('view_metricscreenset')"
                         @change="updatePermission(user.email, 'view_metricscreenset', $event.target.checked)">
                </td>
                <td>
                  <input type="checkbox" 
                         :checked="user.permissions.includes('change_metricscreenset')"
                         @change="updatePermission(user.email, 'change_metricscreenset', $event.target.checked)">
                </td>
              </tr>
              <!-- Add new user row -->
              <tr>
                <td>
                  <div class="select is-fullwidth">
                    <select v-model="newUserEmail" @focus="loadUserOptions">
                      <option value="">Select user...</option>
                      <option v-for="email in filteredUsers" :key="email" :value="email">
                        {{ email }}
                      </option>
                    </select>
                  </div>
                </td>
                <td>
                  <input type="checkbox" v-model="newUserCanView">
                </td>
                <td>
                  <input type="checkbox" v-model="newUserCanEdit">
                </td>
                <td>
                  <button class="button is-small is-primary" 
                          @click="addPermission" 
                          :disabled="!newUserEmail">
                    Add
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Metrics section -->
        <div class="mb-4">
          <h4 class="title is-6">Metrics ({{ valueSet.metrics.length }})</h4>
          <div class="tags">
            <span 
              v-for="metric in valueSet.metrics" 
              :key="metric.id"
              :class="['tag', 'is-light', 'is-' + getMetricCategory(metric)]"
            >
              {{ metric.details?.name }}
            </span>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="buttons">
          <button class="button is-info" v-if="isEditable" @click="$emit('edit', valueSet)">
            <span class="icon">
              <i class="fas fa-edit"></i>
            </span>
            <span>Edit Values Set</span>
          </button>
          <button class="button is-danger is-light" v-if="isEditable" @click="confirmDelete">
            <span class="icon">
              <i class="fas fa-trash"></i>
            </span>
            <span>Delete</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ValueSetCard',
  props: {
    valueSet: {
      type: Object,
      required: true
    },
    editableSets: {
      type: Set,
      default: () => new Set()
    },
  },
  data() {
    return {
      isExpanded: false,
      permissions: null,
      isLoadingPermissions: false,
      newUserEmail: '',
      newUserCanView: true,
      newUserCanEdit: false,
      userOptions: [],
      filteredUsers: [],
    }
  },
  computed: {
    isEditable() {
      return this.editableSets && this.editableSets instanceof Set && this.editableSets.has(this.valueSet.id);
    },
  },
  methods: {
    formatDate(date) {
      return date ? new Date(date).toLocaleDateString() : '';
    },
    getMetricCategory(metric) {
      return metric?.details?.subcategory?.[0]?.esgissue?.slug || 'default';
    },
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded && this.isEditable && !this.permissions) {
        this.loadPermissions();
      }
    },
    async loadPermissions() {
      this.isLoadingPermissions = true;
      try {
        const response = await fetch(`/api/v1/screen_sets/${this.valueSet.id}/permissions/`);
        if (!response.ok) throw new Error('Failed to fetch permissions');
        this.permissions = await response.json();
      } catch (error) {
        console.error('Error loading permissions:', error);
        this.$buefy.toast.open({
          message: 'Failed to load permissions',
          type: 'is-danger'
        });
      } finally {
        this.isLoadingPermissions = false;
      }
    },
    async loadUserOptions() {
      try {
        const response = await fetch('/api/v1/users/share_permissions/');
        const { request_user, permission_users } = await response.json();
        // Filter out the request_user from the permission_users array
        this.filteredUsers = permission_users.filter(user => user !== request_user);
      } catch (error) {
        console.error('Failed to load users:', error);
      }
    },
    async updatePermission(userEmail, permission, value) {
      try {
        const action = value ? 'add' : 'remove';
        const response = await fetch(`/api/v1/screen_sets/${this.valueSet.id}/permissions/${action}/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            email: userEmail,
            permission: permission,
          })
        });
        if (!response.ok) throw new Error('Failed to update permission');
        await this.loadPermissions();
        
        // Show save indicator
        this.$buefy.toast.open({
          message: 'Permissions updated successfully',
          type: 'is-success'
        });
      } catch (error) {
        console.error('Error updating permission:', error);
        this.$buefy.toast.open({
          message: 'Failed to update permission',
          type: 'is-danger'
        });
      }
    },
    async addPermission() {
      if (!this.newUserEmail) return;
      
      const permissions = [];
      if (this.newUserCanView) permissions.push('view_metricscreenset');
      if (this.newUserCanEdit) permissions.push('change_metricscreenset');
      
      try {
        for (const permission of permissions) {
          await this.updatePermission(this.newUserEmail, permission, true);
        }
        this.newUserEmail = '';
        this.newUserCanView = true;
        this.newUserCanEdit = false;
      } catch (error) {
        console.error('Failed to add permission:', error);
      }
    },
    confirmDelete() {
      this.$buefy.dialog.confirm({
        title: 'Delete Values Set',
        message: 'Are you sure you want to delete this values set?',
        confirmText: 'Delete',
        type: 'is-danger',
        onConfirm: () => this.$emit('delete', this.valueSet.id)
      });
    }
  },
}
</script>

<style scoped>
.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
}

.card-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.card-header:hover {
  background-color: #f9f9f9;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>